import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useContext, useEffect } from 'react';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import TeamAccess from './TeamAccess';
import RoleManagement from './RoleManagement';
import PolicyManagement from './PolicyManagement';
import ApplicationManagement from './ApplicationManagement';
import UserGroups from './UserGroups';
import Theme from './Theme';
import Configuration from './Configuration';
import { isAuthorized } from '../utils/utilities';
import { SubmitSettings, ConfigContext } from '../components';
import authenticatedRoute from '../views/AuthenticatedRoute';
import { constants } from '../constants';
import Cookies from 'js-cookie';
function CustomTabPanel({ children, value, index, ...other }) {
    return (_jsx("div", { role: "tabpanel", hidden: value !== index, id: `settings-tabpanel-${index}`, "aria-labelledby": `settings-tab-${index}`, ...other, children: _jsx(Box, { children: children }) }));
}
const Settings = () => {
    const { configState, setConfigState } = useContext(ConfigContext);
    const [settings, setSettings] = useState(configState?.settings || null);
    // Sync settings when configState changes
    useEffect(() => {
        setSettings(configState?.settings || null);
        Cookies.remove('Product Selected');
    }, [configState?.settings]);
    // Auto-select product ID 1 when on /settings route
    useEffect(() => {
        setConfigState((prevState) => ({ ...prevState, selectedProduct: '0' }));
        Cookies.set('Product Selected', '0', {
            secure: true,
            sameSite: 'Strict'
        });
    }, [setConfigState]);
    // Define available tabs with their permissions
    const availableTabs = [
        {
            label: constants.settings_tab_team_access,
            component: _jsx(TeamAccess, {}),
            permission: 'RBAC:ViewUser'
        },
        {
            label: constants.settings_tab_user_groups,
            component: _jsx(UserGroups, {}),
            permission: 'RBAC:ViewUserGroup'
        },
        {
            label: constants.settings_tab_roles,
            component: _jsx(RoleManagement, {}),
            permission: 'RBAC:ViewRole'
        },
        {
            label: constants.settings_tab_policies,
            component: _jsx(PolicyManagement, {}),
            permission: 'RBAC:ViewPolicy'
        },
        {
            label: 'Applications',
            component: _jsx(ApplicationManagement, {}),
            permission: 'RBAC:ViewApplication'
        },
        {
            label: constants.settings_tab_theme,
            component: _jsx(Theme, { settings: settings, setSettings: setSettings }),
            permission: 'RBAC:UpdateConfiguration'
        },
        {
            label: constants.settings_tab_configuration,
            component: _jsx(Configuration, {}),
            permission: 'RBAC:ViewConfiguration'
        }
    ];
    // Filter tabs based on permissions
    const visibleTabs = availableTabs.filter((tab) => tab.permission === null || isAuthorized(tab.permission));
    const [tabValue, setTabValue] = useState(() => {
        // Start with the first available tab
        return visibleTabs.length > 0 ? 0 : -1;
    });
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };
    // If no tabs are visible, show an access denied message
    if (visibleTabs.length === 0) {
        return (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                        bgcolor: '#ffffff',
                        p: 3,
                        borderRadius: 2,
                        borderBottom: '1px solid #ebebeb',
                        height: 99
                    }, children: _jsx(Box, { sx: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2
                        }, children: _jsx(Typography, { variant: "h4", color: "#212121", children: constants.settings_page_title }) }) }), _jsx(Box, { sx: { p: 3, bgcolor: '#f8f8f8' }, children: _jsxs(Box, { sx: { textAlign: 'center', py: 8 }, children: [_jsx(Typography, { variant: "h6", color: "#666", gutterBottom: true, children: constants.settings_no_access_title }), _jsx(Typography, { variant: "body1", color: "#888", children: constants.settings_no_access_desc })] }) })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                    bgcolor: '#ffffff',
                    p: 3,
                    borderRadius: 2,
                    borderBottom: '1px solid #ebebeb',
                    height: 99
                }, children: _jsxs(Box, { sx: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }, children: [_jsx(Typography, { variant: "h4", color: "#212121", children: constants.settings_page_title }), _jsx(SubmitSettings, { settings: settings, setSettings: setSettings, selectedTab: visibleTabs[tabValue]?.label })] }) }), _jsx(Box, { sx: { p: 3, bgcolor: '#f8f8f8' }, children: _jsxs(Box, { children: [_jsx(Box, { sx: {
                                borderBottom: '1px solid #ebebeb',
                                px: 3,
                                pt: 2
                            }, children: _jsx(Tabs, { value: tabValue, onChange: handleTabChange, "aria-label": "settings tabs", sx: {
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: '#26890D'
                                    }
                                }, children: visibleTabs.map((tab) => (_jsx(Tab, { label: tab.label, sx: {
                                        textTransform: 'none',
                                        '&.Mui-selected': {
                                            color: '#26890D'
                                        },
                                        '&:hover': {
                                            color: '#26890D',
                                            backgroundColor: 'rgba(38, 137, 13, 0.04)'
                                        }
                                    } }, tab.label))) }) }), visibleTabs.map((tab, index) => (_jsx(CustomTabPanel, { value: tabValue, index: index, children: tab.component }, tab.label)))] }) })] }));
};
export default authenticatedRoute(Settings);
